import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Editor from "@monaco-editor/react";
import { useEditorTheme } from "../theme.js";
import CourseChrome from "../components/CourseChrome.js";
import { useAuth } from "../auth.js";
import { api, type Course, type RunResult } from "../api.js";

/**
 * A compiler with nothing attached to it: write C, run it, read the output.
 *
 * Everything else in the app compiles code that belongs to a question, which
 * makes the most ordinary thing in a lab -- a teacher showing what a loop does,
 * a student trying a language feature before they use it in an answer --
 * something you can only do by creating an assignment nobody will submit.
 *
 * Nothing here is graded, marked, or kept on the server. The code lives in this
 * browser so a reload does not throw the demonstration away mid-class.
 */

const STORAGE_KEY = "codebench:playground";

const STARTER = `#include <stdio.h>

int main(void) {
    printf("Hello, world!\\n");
    return 0;
}
`;

/**
 * Snippets a teacher reaches for at the whiteboard. They are here because the
 * alternative in a live class is typing twenty lines while sixty people watch.
 */
const EXAMPLES: { name: string; code: string; stdin?: string }[] = [
  { name: "Hello world", code: STARTER },
  {
    name: "Read input",
    code: `#include <stdio.h>

int main(void) {
    int a, b;
    scanf("%d %d", &a, &b);
    printf("%d\\n", a + b);
    return 0;
}
`,
    stdin: "3 4\n",
  },
  {
    name: "Loop",
    code: `#include <stdio.h>

int main(void) {
    for (int i = 1; i <= 5; i++) {
        printf("%d squared is %d\\n", i, i * i);
    }
    return 0;
}
`,
  },
  {
    name: "Pointers",
    code: `#include <stdio.h>

void swap(int *x, int *y) {
    int t = *x;
    *x = *y;
    *y = t;
}

int main(void) {
    int a = 1, b = 2;
    printf("before: a=%d b=%d\\n", a, b);
    swap(&a, &b);
    printf("after:  a=%d b=%d\\n", a, b);
    return 0;
}
`,
  },
  {
    name: "Undefined behaviour",
    code: `#include <stdio.h>

int main(void) {
    int values[3] = {1, 2, 3};
    /* Off the end of the array. C will not stop you; the result is whatever
       happens to sit in that memory. */
    printf("%d\\n", values[5]);
    return 0;
}
`,
  },
];

type Tab = "output" | "compiler";

export default function Playground() {
  const editorTheme = useEditorTheme();
  const { user } = useAuth();
  const [params] = useSearchParams();
  // Carried by the tab that brought you here, so the Assignments tab beside it
  // goes back to the course you were in rather than to whatever "/" defaults to.
  const fromCourse = params.get("course");
  const [courses, setCourses] = useState<Course[]>([]);

  const saved = (() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null") as
        | { code: string; stdin: string }
        | null;
    } catch {
      return null;
    }
  })();

  const [code, setCode] = useState(saved?.code ?? STARTER);
  const [stdin, setStdin] = useState(saved?.stdin ?? "");
  const [tab, setTab] = useState<Tab>("output");
  const [busy, setBusy] = useState<null | "compile" | "run">(null);
  const [error, setError] = useState<string | null>(null);
  const [diagnostics, setDiagnostics] = useState<string | null>(null);
  const [compileOk, setCompileOk] = useState<boolean | null>(null);
  const [runResult, setRunResult] = useState<RunResult | null>(null);
  // What the sandbox will allow. Worth stating up front rather than after a
  // program is killed for reasons the student cannot see.
  const [limits, setLimits] = useState<{ timeLimitMs: number; memoryLimitKb: number } | null>(null);

  useEffect(() => {
    api
      .get<{ limits: { timeLimitMs: number; memoryLimitKb: number } }>("/api/playground/limits")
      .then((r) => setLimits(r.limits))
      .catch(() => {});

    // Only to name the course in the header; the playground belongs to none.
    if (fromCourse) {
      api
        .get<{ courses: Course[] }>("/api/courses")
        .then((r) => setCourses(r.courses))
        .catch(() => {});
    }
  }, [fromCourse]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ code, stdin }));
    } catch {
      // A full or disabled localStorage costs the reload safety net, nothing more.
    }
  }, [code, stdin]);

  const compile = useCallback(async () => {
    setBusy("compile");
    setError(null);
    setTab("compiler");
    try {
      const r = await api.post<{ ok: boolean; diagnostics: string }>("/api/playground/compile", { code });
      setCompileOk(r.ok);
      setDiagnostics(r.diagnostics);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Compile failed.");
    } finally {
      setBusy(null);
    }
  }, [code]);

  const run = useCallback(async () => {
    setBusy("run");
    setError(null);
    try {
      const r = await api.post<{ compiled: boolean; diagnostics: string; run: RunResult | null }>(
        "/api/playground/run",
        { code, stdin }
      );
      setCompileOk(r.compiled);
      setDiagnostics(r.diagnostics);
      setRunResult(r.run);
      // Errors are on the compiler tab, output is on the output tab; go to
      // whichever one actually has the answer.
      setTab(r.compiled ? "output" : "compiler");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Run failed.");
    } finally {
      setBusy(null);
    }
  }, [code, stdin]);

  // Same shortcut as the assignment workspace, so the habit carries over.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        if (!busy) void run();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, run]);

  function loadExample(name: string) {
    const example = EXAMPLES.find((x) => x.name === name);
    if (!example) return;
    if (code.trim() && code !== STARTER && !confirm("Replace what is in the editor?")) return;
    setCode(example.code);
    setStdin(example.stdin ?? "");
    setRunResult(null);
    setDiagnostics(null);
    setCompileOk(null);
  }

  return (
    <div className="playground">
      {/* The same tab strip the course pages carry, so Assignments and
          Playground are two places rather than a page and a top-bar link. */}
      <div className="playground-head">
        <CourseChrome
          section="playground"
          course={courses.find((c) => c.id === fromCourse) ?? null}
          staff={user?.role === "TEACHER" || user?.role === "UNIVERSITY_ADMIN"}
        />
      </div>

      <div className="editor-column">
        <div className="toolbar">
          <span className="muted" style={{ fontSize: 12.5 }}>
            C
            {limits && <> · {limits.timeLimitMs} ms · {Math.round(limits.memoryLimitKb / 1024)} MB</>}
          </span>
          <select
            value=""
            onChange={(e) => {
              loadExample(e.target.value);
              e.target.value = "";
            }}
          >
            <option value="">Load an example…</option>
            {EXAMPLES.map((x) => (
              <option key={x.name} value={x.name}>{x.name}</option>
            ))}
          </select>
          <div style={{ flex: 1 }} />
          {compileOk !== null && (
            <span className={`badge ${compileOk ? "pass" : "fail"}`}>
              {compileOk ? "compiles" : "compile error"}
            </span>
          )}
          <button onClick={compile} disabled={busy !== null}>
            {busy === "compile" ? "Compiling…" : "Compile"}
          </button>
          <button className="primary" onClick={run} disabled={busy !== null}>
            {busy === "run" ? "Running…" : "Run ▸"}
          </button>
        </div>

        <Editor
          language="c"
          theme={editorTheme}
          value={code}
          onChange={(v) => setCode(v ?? "")}
          options={{
            fontSize: 13.5,
            minimap: { enabled: false },
            tabSize: 4,
            insertSpaces: true,
            scrollBeyondLastLine: false,
            automaticLayout: true,
            renderWhitespace: "selection",
          }}
        />

        <div className="output">
          <div className="tabs">
            <button className={tab === "output" ? "active" : ""} onClick={() => setTab("output")}>
              Output
            </button>
            <button className={tab === "compiler" ? "active" : ""} onClick={() => setTab("compiler")}>
              Compiler
            </button>
          </div>

          <div className="output-body">
            {error && <div className="error-banner">{error}</div>}

            {tab === "compiler" && (
              <pre className={`console ${diagnostics ? "" : "empty"}`}>
                {diagnostics || "Warnings and errors from the compiler appear here."}
              </pre>
            )}

            {tab === "output" && (
              <div className="grid-2">
                <label style={{ marginBottom: 0 }}>
                  <span>Input (stdin)</span>
                  <textarea rows={6} value={stdin} onChange={(e) => setStdin(e.target.value)} />
                </label>
                <div>
                  <span className="muted" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                    Output
                    {runResult && (
                      <> · {runResult.timeMs} ms
                        {runResult.timedOut && " · timed out"}
                        {runResult.signal && ` · ${runResult.signal}`}
                        {runResult.exitCode !== null && ` · exit ${runResult.exitCode}`}
                      </>
                    )}
                  </span>
                  <pre className={`console ${runResult?.stdout ? "" : "empty"}`}>
                    {runResult
                      ? runResult.stdout || "(no output)"
                      : "Press Run — Ctrl/Cmd+Enter also works."}
                  </pre>
                  {runResult?.stderr && (
                    <pre className="console" style={{ marginTop: 6, color: "var(--fail-text)" }}>
                      {runResult.stderr}
                    </pre>
                  )}
                  {runResult?.outputTruncated && (
                    <div className="notice" style={{ marginTop: 6 }}>
                      Output was too long and got cut off.
                    </div>
                  )}
                  {runResult?.timedOut && (
                    <div className="notice" style={{ marginTop: 6 }}>
                      The program ran past its time limit and was stopped. An infinite loop, or
                      waiting on input that was never given?
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
